import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calculator } from "@/components/Calculator";
import { estimateDevice } from "@/lib/api";
import { track } from "@/lib/track";

// Lässt Nutzer ein nicht gelistetes Gerät eingeben und per KI schätzen.
// Die KI liefert nur Verbrauchsspannen; gerechnet wird deterministisch im Calculator.
export default function CustomDeviceEstimator({ initialName = "" }) {
  const [name, setName] = useState(initialName);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { ok, device?, status?, message? }

  const submit = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (n.length < 2 || loading) return;
    setLoading(true);
    setResult(null);
    track("ai_estimate", { status: "triggered" });
    const res = await estimateDevice(n);
    if (res.ok && res.cached) {
      track("ai_estimate", { status: "cached" });
    } else if (!res.ok && (res.reason === "ip_limit" || res.reason === "global_limit")) {
      track("ai_estimate", { status: "blocked" });
    }
    setResult(res);
    setLoading(false);
  };

  return (
    <Card className="p-4 sm:p-6 border bg-secondary/30" data-testid="custom-estimator">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="font-heading font-semibold">Gerät nicht dabei? KI-Schätzung</h3>
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        Gib ein beliebiges Elektrogerät ein. Wir schätzen den typischen Verbrauch und rechnen die Kosten aus.
      </p>

      <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="z. B. Standventilator, Poolpumpe, Heizdecke"
          maxLength={60}
          aria-label="Gerätename"
          data-testid="custom-estimator-input"
          className="h-11"
        />
        <Button type="submit" disabled={loading || name.trim().length < 2} className="h-11 shrink-0" data-testid="custom-estimator-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Schätzen"}
        </Button>
      </form>

      {result && !result.ok && (
        <p className="mt-3 text-sm text-muted-foreground" data-testid="custom-estimator-message">
          {result.message || "Das hat leider nicht geklappt."}
        </p>
      )}

      {result && result.ok && result.device && (
        <div className="mt-5" data-testid="custom-estimator-result">
          <div className="flex items-baseline justify-between gap-2 mb-2">
            <h4 className="text-lg font-semibold">{result.device.name}</h4>
            <span className="text-xs text-muted-foreground">KI-Schätzung</span>
          </div>
          {result.device.intro && <p className="text-sm text-muted-foreground mb-4">{result.device.intro}</p>}
          <Calculator device={result.device} />
          <p className="mt-3 text-xs text-muted-foreground">
            Diese Werte sind eine KI-gestützte Schätzung typischer Verbräuche. Für genaue Zahlen hilft ein Strommessgerät.
          </p>
        </div>
      )}
    </Card>
  );
}
